<!-- Adapted from YesPlayMusic v0.4.11 (MIT), upstream Cover.vue.
     Original template/layout/SCSS retained; playback is connected to the local runtime. -->
<template>
  <div
    class="cover"
    :class="{ 'cover-hover': coverHover }"
    @mouseover="focus = true"
    @mouseleave="leaveCover"
    @focusin="focus = true"
    @focusout="leaveFocus"
    @click="clickCoverToPlay ? play() : goTo()"
    @keydown.enter.prevent="clickCoverToPlay ? play() : goTo()"
    role="link"
    tabindex="0"
    :aria-label="'打开' + label"
  >
    <div class="cover-container">
      <div class="shade">
        <button
          v-show="alwaysShowPlayButton && (focus || loading)"
          class="play-button"
          type="button"
          :disabled="loading"
          :aria-label="'播放' + label"
          :style="playButtonStyles"
          @keydown.enter.stop
          @click.stop="play()"
          ><svg-icon icon-class="play" />
        </button>
      </div>
      <ArtworkImage :src="imageUrl" :fallback="fallbackCover" :retry-key="imageRetryKey" :alt="label" :style="imageStyles" loading="lazy" @source-error="$emit('source-error', $event)" />
      <transition v-if="coverHover || alwaysShowShadow" name="fade">
        <div
          v-show="focus || alwaysShowShadow"
          class="shadow"
          :style="shadowStyles"
        ></div>
      </transition>
    </div>
  </div>
</template>

<script>
import SvgIcon from './SvgIcon.vue';
import ArtworkImage from './ArtworkImage.vue';
import { runtime } from '../bridge';
import { matchesLibrarySource } from '../library-source';
import { matchesAlbumRoute } from '../album-identity';
import { artistNavigation, collectArtistSongs, collectCatalogTracks } from '../artist-navigation';
import { workspace, workspaceAllowsTrack } from '../workspace';

export default {
  components: { SvgIcon, ArtworkImage },
  props: {
    id: { type: [Number, String], required: true },
    artist: { type: Object, default: null },
    label: { type: String, default: '音乐合集' },
    tracks: { type: Array, default: null },
    sourceFilter: { type: String, default: 'all' },
    type: { type: String, required: true },
    imageUrl: { type: String, required: true },
    imageRetryKey: { type: [String, Number], default: '' },
    fixedSize: { type: Number, default: 0 },
    playButtonSize: { type: Number, default: 22 },
    coverHover: { type: Boolean, default: true },
    alwaysShowPlayButton: { type: Boolean, default: true },
    alwaysShowShadow: { type: Boolean, default: false },
    clickCoverToPlay: { type: Boolean, default: false },
    shadowMargin: { type: Number, default: 12 },
    radius: { type: Number, default: 12 },
  },
  data() {
    return {
      focus: false,
      loading: false,
      fallbackCover: runtime.cover(),
    };
  },
  computed: {
    imageStyles() {
      let styles = {};
      if (this.fixedSize !== 0) {
        styles.width = this.fixedSize + 'px';
        styles.height = this.fixedSize + 'px';
      }
      if (this.type === 'artist') styles.borderRadius = '50%';
      return styles;
    },
    playButtonStyles() {
      let styles = {};
      styles.width = this.playButtonSize + '%';
      styles.height = this.playButtonSize + '%';
      return styles;
    },
    shadowStyles() {
      let styles = {};
      styles.backgroundImage = `url(${this.imageUrl})`;
      if (this.type === 'artist') styles.borderRadius = '50%';
      return styles;
    },
  },
  beforeDestroy() { this._playRequest = (this._playRequest || 0) + 1; },
  methods: {
    leaveCover() { this.focus = this.$el.contains(document.activeElement); },
    leaveFocus(event) { if (!this.$el.contains(event.relatedTarget)) this.focus = false; },
    async play() {
      if (this.loading) return;
      const ticket = this._playRequest = (this._playRequest || 0) + 1;
      const playbackTicket = runtime.reservePlaybackRequest();
      const id = String(this.id), type = this.type, mode = workspace.mode;
      const current = () => ticket === this._playRequest && String(this.id) === id && this.type === type && workspace.mode === mode && runtime.isPlaybackRequestCurrent(playbackTicket);
      this.loading = true;
      try {
        if (this.type === 'artist') {
          const artist = this.artist || { id, name: this.label };
          const target = artistNavigation(artist, mode);
          if (!target.route) throw new Error(target.reason);
          const tracks = mode === 'local' ? runtime.libraryTracks().filter(track => workspaceAllowsTrack(track, 'local') && matchesLibrarySource(track, this.sourceFilter) && track.artists.includes(artist.name)) : await collectArtistSongs(offset => runtime.catalog('artist', decodeURIComponent(target.route.path.slice(8)), { section: 'songs', offset, limit: 100 }));
          if (!current()) return;
          if (!tracks.length) return runtime.notify('这位歌手暂时没有可读取的歌曲');
          await runtime.playTracks(tracks); return;
        }
        let tracks = this.tracks;
        const playlist = this.type === 'playlist' && runtime.state.playlists.find(item => item.id === id);
        if (playlist) tracks = playlist.trackIds.map(trackId => runtime.state.tracks.find(track => track.id === trackId)).filter(Boolean);
        if (this.type === 'playlist' && ['favorites', 'history'].includes(id)) tracks = runtime.state[id].map(trackId => runtime.state.tracks.find(track => track.id === trackId)).filter(Boolean);
        if (this.type === 'playlist' && id === 'local:all') tracks = runtime.state.tracks;
        if (this.type === 'playlist' && id === 'recent') tracks = [...runtime.state.tracks].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
        if (id.startsWith('local:') && ['album', 'artist'].includes(this.type)) {
          let name = id.slice(6);
          try { name = decodeURIComponent(name); } catch { /* names may already be decoded by Vue Router */ }
          tracks = runtime.state.tracks.filter(track => matchesLibrarySource(track, this.sourceFilter) && (this.type === 'album' ? matchesAlbumRoute(track, id) : track.artists.includes(name)));
        }
        if (!tracks) tracks = ['playlist', 'album'].includes(type) ? await collectCatalogTracks(async offset => {
          if (!current()) throw new DOMException('合集已切换', 'AbortError');
          const data = await runtime.catalog(type, id, { offset, limit: 100 });
          if (!current()) throw new DOMException('合集已切换', 'AbortError');
          return data;
        }, type === 'album' ? '专辑' : '歌单') : (await runtime.catalog(type, id)).tracks;
        if (!current()) return;
        if (tracks) tracks = tracks.filter(track => matchesLibrarySource(track, this.sourceFilter));
        if (!tracks?.length) {
          runtime.notify('这个合集暂时没有可播放歌曲');
          return;
        }
        await runtime.playTracks(tracks);
      } catch (error) {
        if (current() && error.name !== 'AbortError') runtime.notify(error.message || '加载歌曲失败，请稍后重试', 'error');
      } finally {
        if (ticket === this._playRequest) this.loading = false;
      }
    },
    goTo() {
      if (this.type === 'artist') { const target = artistNavigation(this.artist || { id: this.id, name: this.label }, workspace.mode); if (target.route) this.$router.push(target.route).catch(() => {}); else runtime.notify(target.reason, 'info'); return; }
      this.$router.push({ name: this.type, params: { id: String(this.id) }, query: this.sourceFilter !== 'all' && (this.type === 'playlist' || String(this.id).startsWith('local:')) ? { source: this.sourceFilter } : {} }).catch(() => {});
    },
  },
};
</script>

<style lang="scss" scoped>
.cover {
  position: relative;
  transition: transform 0.3s;
}
.cover-container {
  position: relative;
}
img {
  border-radius: 0.75em;
  width: 100%;
  user-select: none;
  aspect-ratio: 1 / 1;
  border: 1px solid rgba(0, 0, 0, 0.04);
}

.cover-hover {
  &:hover {
    cursor: pointer;
    /* transform: scale(1.02); */
  }
}

.shade {
  position: absolute;
  top: 0;
  height: calc(100% - 3px);
  width: 100%;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
}
.play-button {
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  backdrop-filter: blur(8px);
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.08);
  height: 22%;
  width: 22%;
  border-radius: 50%;
  cursor: default;
  transition: 0.2s;
  .svg-icon {
    width: 50%;
    margin: {
      left: 4px;
    }
  }
  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }
  &:active {
    transform: scale(0.94);
  }
  &:disabled {
    opacity: 0.5;
    cursor: wait;
  }
}

.shadow {
  position: absolute;
  top: 12px;
  height: 100%;
  width: 100%;
  filter: blur(16px) opacity(0.6);
  transform: scale(0.92, 0.96);
  z-index: -1;
  background-size: cover;
  border-radius: 0.75em;
  aspect-ratio: 1 / 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
